import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Shield, ArrowLeft } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import Logo from "@/components/Logo";

const SuperAdminLogin = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const { toast } = useToast();

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    try {
      localStorage.removeItem('superAdminAuth');
      localStorage.removeItem('userRole');
      localStorage.removeItem('hostAuth');
      localStorage.removeItem('hostId');

      const { data: authData, error: authError } = await supabase.auth.signInWithPassword({
        email: email.trim(),
        password,
      });

      if (authError || !authData?.user) {
        toast({
          title: "Invalid Credentials",
          description: "Check your email and password",
          variant: "destructive",
        });
        return;
      }

      const { data: roleData, error: roleError } = await supabase
        .from('user_roles')
        .select('role')
        .eq('user_id', authData.user.id)
        .eq('role', 'super_admin')
        .maybeSingle();

      if (roleError || !roleData) {
        await supabase.auth.signOut();
        toast({
          title: "Access Denied",
          description: "Your account does not have SuperAdmin privileges",
          variant: "destructive",
        });
        return;
      }

      localStorage.setItem('superAdminAuth', 'true');
      localStorage.setItem('userRole', 'super_admin');

      toast({
        title: "Welcome SuperAdmin",
        description: "Successfully logged in to KaraokeCore",
      });

      navigate('/super-admin');
    } catch (error) {
      console.error('Login error:', error);
      toast({
        title: "Login Failed",
        description: "An error occurred during login",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-console-bg text-console-text font-geist relative">
      {/* Faint 48px grid */}
      <div
        aria-hidden
        className="pointer-events-none fixed inset-0 z-0"
        style={{
          backgroundImage:
            'linear-gradient(to right, rgba(255,255,255,0.025) 1px, transparent 1px), linear-gradient(to bottom, rgba(255,255,255,0.025) 1px, transparent 1px)',
          backgroundSize: '48px 48px',
          maskImage: 'radial-gradient(ellipse at 50% 30%, black 30%, transparent 80%)',
          WebkitMaskImage: 'radial-gradient(ellipse at 50% 30%, black 30%, transparent 80%)',
        }}
      />

      <div className="relative z-10 min-h-screen flex flex-col">
        {/* Header */}
        <header className="border-b border-console-line bg-console-bg/85 backdrop-blur-sm">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
            <button onClick={() => navigate('/')} className="flex items-center gap-3 min-h-[44px]">
              <div className="relative">
                <Logo tone="text" size="md" />
                <span className="absolute -top-0.5 -right-0.5 w-1.5 h-1.5 rounded-full bg-console-red shadow-[0_0_6px_#FF5A5F]" />
              </div>
              <div className="flex flex-col leading-none text-left">
                <span className="font-geist font-semibold text-[15px] tracking-tight text-console-text">KaraokeCore</span>
                <span className="font-geist-mono text-[10px] tracking-[0.16em] uppercase text-console-muted mt-1">SuperAdmin</span>
              </div>
            </button>

            <Button
              variant="ghost"
              onClick={() => navigate('/')}
              className="h-11 sm:h-9 px-3 sm:px-4 text-[13px] font-medium font-geist text-console-text hover:bg-white/5 hover:text-console-text border border-console-line-strong rounded-[6px]"
            >
              <ArrowLeft className="h-4 w-4 mr-2" />
              <span className="hidden sm:inline">Back to Home</span>
              <span className="sm:hidden">Back</span>
            </Button>
          </div>
        </header>

        {/* Form */}
        <main className="flex-1 flex items-center justify-center p-4 sm:p-6">
          <div className="w-full max-w-md">
            <div className="bg-console-surface border border-console-line rounded-[12px] p-7 sm:p-9">
              <div className="font-geist-mono text-[11px] tracking-[0.18em] uppercase text-console-red mb-4 flex items-center gap-2">
                <Shield className="h-3.5 w-3.5" />
                00 / SuperAdmin
              </div>
              <h1 className="font-geist font-semibold text-[36px] leading-none tracking-[-0.03em] mb-3">
                Platform login
              </h1>
              <p className="font-geist text-[14px] leading-relaxed text-console-muted mb-7">
                Restricted to KaraokeCore platform administrators.
              </p>

              <form onSubmit={handleLogin} className="space-y-4">
                <div className="space-y-1.5">
                  <Label htmlFor="email" className="font-geist-mono text-[10px] tracking-[0.16em] uppercase text-console-muted">
                    Email
                  </Label>
                  <Input
                    id="email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="admin@nairoke.com"
                    className="h-12 bg-console-bg border-console-line-strong text-console-text placeholder:text-console-dim font-geist text-[15px] rounded-[6px] focus:border-console-red focus-visible:ring-0 focus-visible:ring-offset-0"
                    required
                  />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="password" className="font-geist-mono text-[10px] tracking-[0.16em] uppercase text-console-muted">
                    Password
                  </Label>
                  <Input
                    id="password"
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="••••••••"
                    className="h-12 bg-console-bg border-console-line-strong text-console-text placeholder:text-console-dim font-geist text-[15px] rounded-[6px] focus:border-console-red focus-visible:ring-0 focus-visible:ring-offset-0"
                    required
                  />
                </div>
                <Button
                  type="submit"
                  className="w-full h-12 px-5 mt-3 text-[14px] font-semibold font-geist bg-console-red text-console-bg hover:bg-console-red/90 rounded-[6px]"
                  disabled={loading}
                >
                  {loading ? "Signing in…" : "Sign in"}
                  <span className="font-geist-mono text-[11px] opacity-70 ml-2">→</span>
                </Button>
              </form>
            </div>

            <div className="mt-6 text-center font-geist-mono text-[11px] tracking-[0.05em] text-console-dim">
              SuperAdmin access only · Credentials managed via Supabase Auth
            </div>
          </div>
        </main>
      </div>
    </div>
  );
};

export default SuperAdminLogin;
